import { useCallback, useEffect, useMemo, useState } from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import ListItemIcon from "@mui/material/ListItemIcon";
import ListItemText from "@mui/material/ListItemText";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import ToggleButton from "@mui/material/ToggleButton";
import ToggleButtonGroup from "@mui/material/ToggleButtonGroup";
import { Archive, CheckCircle2, CopyPlus, Eye, FilePenLine, Globe2, LockKeyhole, MoreHorizontal, PackageOpen, Pin, RotateCcw, Share2 } from "lucide-react";
import { Link } from "react-router-dom";
import type { Post, PostListingState } from "../../shared/contracts";
import { PostCard } from "../components/PostCard";
import { StatePanel } from "../components/StatePanel";
import { ShareDialog } from "../components/ShareDialog";
import { api } from "../lib/api";
import { toast } from "../lib/toast";

type WarehouseFilter = "all" | PostListingState;

export function WarehousePage() {
  const [posts, setPosts] = useState<Post[]>([]);
  const [filter, setFilter] = useState<WarehouseFilter>("all");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [busyPostId, setBusyPostId] = useState<string | null>(null);
  const [sharePost, setSharePost] = useState<Post | null>(null);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [menuPostId, setMenuPostId] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      setPosts((await api<{ items: Post[] }>("/posts/mine")).items);
    } catch (loadError) {
      setError(loadError instanceof Error ? loadError.message : "内容仓库加载失败");
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const counts = useMemo(() => ({
    all: posts.length,
    listed: posts.filter((post) => post.listingState === "listed").length,
    sold: posts.filter((post) => post.listingState === "sold").length,
    stored: posts.filter((post) => post.listingState === "stored").length
  }), [posts]);
  const visiblePosts = filter === "all" ? posts : posts.filter((post) => post.listingState === filter);

  const openActions = (postId: string, anchor: HTMLElement) => {
    setMenuPostId(postId);
    setMenuAnchor(anchor);
  };

  const closeActions = () => {
    setMenuAnchor(null);
    setMenuPostId(null);
  };

  const runAction = (action: () => void | Promise<void>) => {
    closeActions();
    void action();
  };

  const updateListing = async (post: Post, state: PostListingState) => {
    setBusyPostId(post.id);
    try {
      const result = await api<{ post: Post }>(`/posts/${post.id}/listing`, { method: "PATCH", body: JSON.stringify({ state }) });
      setPosts((current) => current.map((item) => item.id === post.id ? result.post : item));
      toast(state === "listed" ? "动态已重新上架" : state === "sold" ? "动态已标记为已售出" : "动态已移入仓库并从前台隐藏", "success");
    } catch (updateError) {
      toast(updateError instanceof Error ? updateError.message : "仓库状态更新失败", "danger");
    } finally {
      setBusyPostId(null);
    }
  };

  const updateVisibility = async (post: Post) => {
    setBusyPostId(post.id);
    try {
      const visibility = post.visibility === "public" ? "self" : "public";
      const result = await api<{ post: Post }>(`/posts/${post.id}/visibility`, { method: "PATCH", body: JSON.stringify({ visibility }) });
      setPosts((current) => current.map((item) => item.id === post.id ? result.post : item));
      toast(visibility === "self" ? "已转为私有动态，仅你和管理员可查看" : "已转为共享动态", "success");
    } catch (updateError) { toast(updateError instanceof Error ? updateError.message : "可见范围更新失败", "danger"); }
    finally { setBusyPostId(null); }
  };

  const updatePin = async (post: Post) => {
    setBusyPostId(post.id);
    try {
      const result = await api<{ post: Post }>(`/posts/${post.id}/profile-pin`, { method: "PATCH", body: JSON.stringify({ pinned: !post.profilePinned }) });
      setPosts((current) => current.map((item) => item.id === post.id ? result.post : result.post.profilePinned ? { ...item, profilePinned: false } : item));
      toast(result.post.profilePinned ? "已置顶到个人主页" : "已取消主页置顶", "success");
    } catch (updateError) { toast(updateError instanceof Error ? updateError.message : "主页置顶更新失败", "danger"); }
    finally { setBusyPostId(null); }
  };

  return (
    <div className="page-shell warehouse-page">
      <header className="warehouse-heading">
        <div><span className="eyebrow">创作者工作区</span><h1>内容仓库</h1><p>集中管理已发布内容和实拍素材。移入仓库不会删除图片，可随时重新上架或复制后再次编辑发布。</p></div>
        <Button component={Link} to="/post/new" variant="contained" startIcon={<CopyPlus aria-hidden="true" />}>发布新动态</Button>
      </header>
      <Alert severity="info" icon={<PackageOpen aria-hidden="true" />}>仓库只保存内容与素材，不提供订单、支付或站内交易功能。</Alert>
      <div className="warehouse-toolbar">
        <ToggleButtonGroup exclusive size="small" value={filter} onChange={(_event, value: WarehouseFilter | null) => value && setFilter(value)} aria-label="仓库内容筛选">
          <ToggleButton value="all">全部 {counts.all}</ToggleButton>
          <ToggleButton value="listed">上架中 {counts.listed}</ToggleButton>
          <ToggleButton value="sold">已售出 {counts.sold}</ToggleButton>
          <ToggleButton value="stored">仓库内 {counts.stored}</ToggleButton>
        </ToggleButtonGroup>
      </div>
      {loading ? <StatePanel kind="loading" title="正在整理内容仓库" /> : null}
      {error ? <StatePanel kind="error" description={error} onRetry={() => void load()} /> : null}
      {!loading && !error && visiblePosts.length === 0 ? <StatePanel kind="empty" title="当前分类没有内容" description="发布的动态会永久保留在这里，方便后续管理和再次使用素材。" /> : null}
      <div className="warehouse-list">
        {visiblePosts.map((post) => (
          <section className="warehouse-item" key={post.id}>
            <header>
              <div className="warehouse-state">
                <Chip color={post.status !== "published" ? "warning" : post.listingState === "listed" ? "success" : "default"} label={post.status !== "published" ? post.status === "pending" ? "审核中" : post.status === "rejected" ? "未通过" : "管理员已下架" : post.listingState === "listed" ? "上架中" : post.listingState === "sold" ? "已售出" : "仅仓库可见"} />
                <Chip variant="outlined" icon={post.visibility === "self" ? <LockKeyhole /> : <Globe2 />} label={post.visibility === "self" ? "私有动态" : "共享动态"} />
                <span><Eye aria-hidden="true" />累计 {post.viewCount} 次浏览</span>
              </div>
            </header>
            <PostCard
              initialPost={post}
              compact
              contentAction={
                <>
                  <Button
                    className="warehouse-actions-trigger"
                    id={`warehouse-actions-trigger-${post.id}`}
                    variant="outlined"
                    size="small"
                    type="button"
                    disabled={busyPostId === post.id}
                    startIcon={<MoreHorizontal aria-hidden="true" />}
                    aria-haspopup="menu"
                    aria-expanded={menuPostId === post.id && Boolean(menuAnchor) ? "true" : "false"}
                    aria-controls={menuPostId === post.id ? `warehouse-actions-menu-${post.id}` : undefined}
                    onClick={(event) => openActions(post.id, event.currentTarget)}
                  >
                    操作
                  </Button>
                  <Menu
                    id={`warehouse-actions-menu-${post.id}`}
                    className="warehouse-actions-menu"
                    anchorEl={menuAnchor}
                    open={menuPostId === post.id && Boolean(menuAnchor)}
                    onClose={closeActions}
                    anchorOrigin={{ vertical: "bottom", horizontal: "right" }}
                    transformOrigin={{ vertical: "top", horizontal: "right" }}
                    slotProps={{ paper: { className: "warehouse-actions-menu-paper" }, list: { "aria-labelledby": `warehouse-actions-trigger-${post.id}` } }}
                  >
                    <MenuItem component={Link} to={`/posts/${post.id}/edit`} onClick={closeActions}>
                      <ListItemIcon><FilePenLine aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="编辑" />
                    </MenuItem>
                    <MenuItem component={Link} to={`/post/new?from=${post.id}`} onClick={closeActions}>
                      <ListItemIcon><CopyPlus aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="复制并重新发布" />
                    </MenuItem>
                    {post.status === "published" ? <MenuItem disabled={busyPostId === post.id} onClick={() => runAction(() => updateVisibility(post))}>
                      <ListItemIcon>{post.visibility === "self" ? <Globe2 aria-hidden="true" /> : <LockKeyhole aria-hidden="true" />}</ListItemIcon>
                      <ListItemText primary={post.visibility === "self" ? "转为共享" : "转为私有"} />
                    </MenuItem> : null}
                    {post.status === "published" ? <MenuItem disabled={busyPostId === post.id} onClick={() => runAction(() => updatePin(post))}>
                      <ListItemIcon><Pin aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary={post.profilePinned ? "取消主页置顶" : "主页置顶"} />
                    </MenuItem> : null}
                    {post.status === "published" && post.shareToken ? <MenuItem onClick={() => runAction(() => setSharePost(post))}>
                      <ListItemIcon><Share2 aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="分享" />
                    </MenuItem> : null}
                    {post.status === "published" && post.listingState !== "listed" ? <MenuItem disabled={busyPostId === post.id} onClick={() => runAction(() => updateListing(post, "listed"))}>
                      <ListItemIcon><RotateCcw aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="重新上架" />
                    </MenuItem> : null}
                    {post.status === "published" && post.listingState === "listed" ? <MenuItem disabled={busyPostId === post.id} onClick={() => runAction(() => updateListing(post, "sold"))}>
                      <ListItemIcon><CheckCircle2 aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="标记已售出" />
                    </MenuItem> : null}
                    {post.status === "published" && post.listingState !== "stored" ? <MenuItem disabled={busyPostId === post.id} onClick={() => runAction(() => updateListing(post, "stored"))}>
                      <ListItemIcon><Archive aria-hidden="true" /></ListItemIcon>
                      <ListItemText primary="移入仓库" />
                    </MenuItem> : null}
                  </Menu>
                </>
              }
            />
          </section>
        ))}
      </div>
      {sharePost?.shareToken ? <ShareDialog open title={sharePost.gameName ? `${sharePost.gameName} · ${sharePost.author.nickname}` : `${sharePost.author.nickname}的动态`} description={sharePost.intro.slice(0, 100)} url={`${window.location.origin}/share/posts/${sharePost.shareToken}`} onClose={() => setSharePost(null)} /> : null}
    </div>
  );
}
